import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Group, RequestQuote, ShoppingCart } from '@mui/icons-material';
import { FACILITY } from '../../../../reusable-components/datagrid/vendorColumns';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import FacilityAccountActions from './FacilityAccountActions';
import { objectToFormData } from '../../../../api/utils';
import axiosInstance from '../../../../utils/axios';
import EditIconButton from '../../../../reusable-components/basic-buttons/EditIconButton';
import FacilityAccountForm from './FacilityAccountForm';

FacilityAccountsTable.propTypes = {
  selectedRow: PropTypes.object,
};

export default function FacilityAccountsTable({ selectedRow }) {
  const { enqueueSnackbar } = useSnackbar();

  const [accounts, setAccounts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [bulkUpdateAccounts, setBulkUpdateAccounts] = useState(false);
  const [isSavingBulk, setIsSavingBulk] = useState(false);
  const [saveType, setSaveType] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [editAccount, setEditAccount] = useState(null);

  const setAccountData = (data) => setAccounts(data ? (selectedRow ? data.filter((d) => d.vendorId === selectedRow.id) : data).map(account => ({
    ...account,
    scopes: [account.isPPD ? "PPD" : "", !account.orderingDisabled ? "Ordering" : "", !account.invoicingDisabled ? "Invoicing" : "", account.onHold ? "On Hold" : ""]
      .filter(area => area).join(", ")
  })) : []);

  const fetchFacilityData = () => {
    setIsLoading(true);
    axiosInstance.get('FacilityAccount/FacilityVendorAccounts')
      .then((res) => {
        setAccountData(res.data.accounts);
        setFacilities(res.data.facilities);
        setVendors(res.data.vendors);
      })
      .catch(() => enqueueSnackbar('Error fetching account data', { variant: "error" }))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { fetchFacilityData(); }, [selectedRow]);

  const deleteFacility = async (id) => {
    axiosInstance.post(`FacilityAccount/DeleteFacilityVendorAccount?id=${id}`)
      .then((res) => {
        if (res.data) {
          enqueueSnackbar('Facility account deleted successfully', { variant: 'success' });
          setAccounts(accounts => accounts.filter(account => account.id !== id))
        } else enqueueSnackbar('Error deleting facility account', { variant: 'error' });
      }).catch(() => enqueueSnackbar('Error deleting facility account', { variant: "error" }))
  };

  const actionButtons = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    id: 'actions',
    width: 70,
    sortable: false,
    renderCell: (params) => {
      const { facility, id, locked, account } = params.row;
      return (
        <>
          <EditIconButton onClick={() => { setOpenForm(true); setEditAccount(params.row); }} disableRipple />
          <ConfirmDialog
            icon="delete-outline"
            tooltip={`Delete Account${locked ? " (Permission Required) " : ""}`}
            color="error"
            content={`Are you sure you want to delete account ${account} for ${facility}?`}
            actionButton="Delete"
            action={() => deleteFacility(id)}
            disabled={locked}
            disableRipple
          />
        </>
      );
    }
  }; 

  const scopes = {
    field: 'scopes',
    headerName: 'Scope',
    width: 105,
    type: 'customSelect',
    renderCell: (params) => <>
      <Tooltip title={params.row.orderingDisabled ? "Ordering Disabled" : params.row.onHold ? "Account On Hold" : "Ordering Enabled"} arrow>
        <IconButton sx={{ p: .3, cursor: "unset" }} disableRipple>
          <ShoppingCart color={!params.row.orderingDisabled ? params.row.onHold ? "warning" : "primary" : "disabled"} />
        </IconButton>
      </Tooltip>
      <Tooltip title={params.row.invoicingDisabled ? "Invoicing Disabled" : "Invoicing Enabled"} arrow>
        <IconButton sx={{ p: .3, cursor: "unset" }} disableRipple>
          <RequestQuote color={!params.row.invoicingDisabled ? "primary" : "disabled"} />
        </IconButton>
      </Tooltip>
      <Tooltip title={params.row.isPPD ? "PPD Account" : "Non PPD Account"} arrow>
        <IconButton sx={{ p: .3, cursor: "unset" }} disableRipple>
          <Group color={params.row.isPPD ? "primary" : "disabled"} />
        </IconButton>
      </Tooltip>
    </>
  };

  const onBulkSave = (type) => {
    setIsSavingBulk(true);
    setSaveType(type);
    axiosInstance.post("FacilityAccount/FacilityVendorAccountBulkToggle", objectToFormData({
      type, accounts: selectedAccounts
    })).then((res) => {
      enqueueSnackbar('Accounts updated successfully', { variant: 'success' });
      setAccountData(res.data);
      setSaveType(null);
      setBulkUpdateAccounts(false);
      setSelectedAccounts([]);
    }).catch(() => enqueueSnackbar('Accounts failed to update', { variant: 'error' }))
      .finally(() => setIsSavingBulk(false));
  }

  const customToolbar = useCallback(
    () => (
      <FacilityAccountActions
        onBulkSave={onBulkSave}
        isSavingBulk={isSavingBulk}
        bulkUpdateAccounts={bulkUpdateAccounts}
        setBulkUpdateAccounts={setBulkUpdateAccounts}
        setIsSavingBulk={setIsSavingBulk}
        saveType={saveType}
        onCancel={() => {
          setSelectedAccounts([]);
          setBulkUpdateAccounts(false);
        }}
        setOpenForm={setOpenForm}
        hasSelection={selectedAccounts.length > 0}
      />
    ),
    [saveType, selectedAccounts, isSavingBulk, bulkUpdateAccounts]
  );

  return <>
    <CustomDataGrid
      gridId="admin-vendors-facility-accounts-view"
      boxSX={{ height: 'calc(100vh - 280px)' }}
      data={accounts}
      gridColumns={[...FACILITY, scopes, actionButtons]}
      CustomLeftToolbar={customToolbar}
      isLoading={isLoading}
      disableSelectionOnClick
      isModal
      checkboxSelection={bulkUpdateAccounts}
      onRowSelectionModelChange={(newSelection) => { setSelectedAccounts(newSelection); }}
      rowSelectionModel={selectedAccounts}
      checkboxTooltipText="Permission Required"
      checkboxTooltipCheck={(row) => row.locked}
      isRowSelectable={(params) => !params.row.locked}
    />
    <FacilityAccountForm vendors={vendors} facilities={facilities} openModal={openForm} editAccount={editAccount} setFacilityAccounts={setAccounts} close={() => { setOpenForm(false); setEditAccount(null); }} />
  </>;
}
